/**
 * Query language parser. Pure: safe to import from the browser (`@plannello/core/query/parse`).
 *
 * Grammar (whitespace separated clauses, all ANDed):
 *   clause   := ["-"] (field | phrase | word)          "-" negates the clause
 *   field    := name ":" [op] value ("," value)*       comma = OR within the field
 *   op       := ">=" | "<=" | ">" | "<" | "="          only for priority and date fields
 *   value    := '"' chars '"' | chars-without-space-or-comma
 *   phrase   := '"' chars '"'                          free text (substring of title, or full-text)
 *   word     := chars-without-space                    free text
 *   sort     := "sort:" key ["-asc" | "-desc"]         rank, priority, created, updated, due, number, title,
 *               or the slug of a single-select label group (`sort:phase`: by label position, none last)
 *
 * Fields: is:open|closed|blocked|mine|unassigned|archived · has:parent|children|assignee|label|milestone|due|start|estimate
 *   assignee:me|none|<email|name|id> · reporter:… · type:<slug|name> · status:<slug|name|category|open|closed>
 *   priority:p0..p3|urgent|high|medium|low|none (with >= etc.; ">=p1" means P1 or more urgent)
 *   product:<key> · space:<key|name> · label:<slug|group/slug|group/*|name|none> · parent:<KEY|none>
 *   milestone:<name|version|none> · key:<KEY>
 *   created|updated|completed|due|start:[op]<YYYY-MM-DD|today|yesterday|tomorrow|N(h|d|w|m|y)>
 * Label groups (Fields): any group slug works as a field key, resolved by `resolveQuery` against the schema:
 *   area:auth  ·  -area:auth  ·  area:auth,checkout (OR)  ·  area:none (no label of the group)  ·  area:* (any label)
 *   Built-in keys always win over a group with the same slug; `label:group/x` still reaches that group.
 * Other unknown "name:" prefixes are treated as free text and reported as a warning (the parser cannot
 * know the groups, so it keeps such words as text clauses carrying a `fieldRef` for the resolver).
 */
import {
  type CompareOp,
  QUERY_FIELDS,
  QUERY_SORT_KEYS,
  type QueryAst,
  type QueryClause,
  type QueryField,
  type QuerySort,
  type QuerySortKey,
  type QueryWarning,
} from "@plannello/shared";

/** Longer query text is cut here and reported as a `query_too_long` warning. */
export const MAX_QUERY_CHARS = 4000;
/** Clauses (every whitespace-separated token, sort keys included) beyond this are ignored with a `too_many_clauses` warning. */
export const MAX_QUERY_CLAUSES = 50;
/** Values of one `field:a,b,c` clause beyond this are ignored with a `too_many_clauses` warning. */
export const MAX_CLAUSE_VALUES = 50;

const shorten = (s: string) => (s.length > 80 ? `${s.slice(0, 80)}...` : s);

const ALIASES: Record<string, QueryField> = {
  assignees: "assignee",
  labels: "label",
  types: "type",
  statuses: "status",
  products: "product",
  milestones: "milestone",
  started: "start",
};

/** Fields that accept comparison operators. */
export const COMPARABLE_FIELDS: ReadonlySet<QueryField> = new Set([
  "priority",
  "created",
  "updated",
  "completed",
  "due",
  "start",
]);

/** Default direction per sort key. For priority "desc" means most urgent first. */
export const DEFAULT_SORT_DIR: Record<QuerySortKey, "asc" | "desc"> = {
  rank: "asc",
  priority: "desc",
  created: "desc",
  updated: "desc",
  due: "asc",
  number: "asc",
  title: "asc",
  group: "asc",
};

function fieldName(name: string): QueryField | "sort" | null {
  const n = name.toLowerCase();
  if (n === "sort") return "sort";
  if ((QUERY_FIELDS as readonly string[]).includes(n)) return n as QueryField;
  return ALIASES[n] ?? null;
}

function readQuoted(s: string, start: number): { value: string; end: number; unterminated: boolean } {
  const close = s.indexOf('"', start + 1);
  if (close < 0) return { value: s.slice(start + 1), end: s.length, unterminated: true };
  return { value: s.slice(start + 1, close), end: close + 1, unterminated: false };
}

const isSpace = (c: string | undefined) => c !== undefined && /\s/.test(c);

function parseSort(value: string): QuerySort | null {
  const m = /^([a-z0-9_]+(?:-[a-z0-9_]+)*?)(?:-(asc|desc))?$/i.exec(value);
  if (!m) return null;
  const key = m[1]!.toLowerCase();
  const dir = m[2]?.toLowerCase() as "asc" | "desc" | undefined;
  if (!(QUERY_SORT_KEYS as readonly string[]).includes(key)) {
    // Not a built-in key: maybe a label group slug; the resolver checks it against the schema.
    return { key: "group", group: key, dir: dir ?? DEFAULT_SORT_DIR.group };
  }
  const k = key as QuerySortKey;
  return { key: k, dir: dir ?? DEFAULT_SORT_DIR[k] };
}

const FIELD_KEY = /([A-Za-z_][A-Za-z0-9_-]*):/y;
const COMPARE_OP = /(>=|<=|>|<|=)/y;

/**
 * Parses query text. Input is capped at MAX_QUERY_CHARS (the rest is dropped with a `query_too_long`
 * warning) and at MAX_QUERY_CLAUSES clauses (the rest is dropped with `too_many_clauses`), so a hostile
 * query costs a bounded amount of parsing, resolving and SQL, here and in the browser alike.
 */
export function parseQuery(input: string): QueryAst {
  const clauses: QueryClause[] = [];
  const sort: QuerySort[] = [];
  const warnings: QueryWarning[] = [];
  let s = input;
  if (s.length > MAX_QUERY_CHARS) {
    warnings.push({ code: "query_too_long", raw: shorten(s.slice(MAX_QUERY_CHARS)) });
    s = s.slice(0, MAX_QUERY_CHARS);
  }
  let i = 0;
  let tokens = 0;

  while (i < s.length) {
    if (isSpace(s[i])) {
      i++;
      continue;
    }
    if (++tokens > MAX_QUERY_CLAUSES) {
      warnings.push({ code: "too_many_clauses", raw: shorten(s.slice(i)) });
      break;
    }
    const start = i;
    let negate = false;
    if (s[i] === "-" && i + 1 < s.length && !isSpace(s[i + 1])) {
      negate = true;
      i++;
    }
    const i0 = i;

    if (s[i] === '"') {
      const q = readQuoted(s, i);
      i = q.end;
      if (q.unterminated) warnings.push({ code: "unterminated_quote", raw: s.slice(start, i) });
      const text = q.value.trim();
      if (text) clauses.push({ kind: "text", text, negate, raw: s.slice(start, i) });
      continue;
    }

    FIELD_KEY.lastIndex = i;
    const m = FIELD_KEY.exec(s);
    const field = m ? fieldName(m[1]!) : null;
    if (m) {
      i += m[0].length;
      let op: CompareOp = "=";
      COMPARE_OP.lastIndex = i;
      const opMatch = field ? COMPARE_OP.exec(s) : null;
      if (opMatch) {
        op = opMatch[1] as CompareOp;
        i += op.length;
      }
      const values: string[] = [];
      for (;;) {
        if (s[i] === '"') {
          const q = readQuoted(s, i);
          if (q.unterminated) warnings.push({ code: "unterminated_quote", raw: s.slice(start, q.end) });
          values.push(q.value);
          i = q.end;
        } else {
          let j = i;
          while (j < s.length && !isSpace(s[j]) && s[j] !== ",") j++;
          values.push(s.slice(i, j));
          i = j;
        }
        if (s[i] === ",") {
          i++;
          continue;
        }
        break;
      }
      // Swallow anything glued to a closing quote (`label:"a"b`) instead of starting a new clause.
      while (i < s.length && !isSpace(s[i])) i++;
      const raw = s.slice(start, i);
      let vals = values.map((v) => v.trim()).filter(Boolean);
      if (vals.length > MAX_CLAUSE_VALUES) {
        warnings.push({ code: "too_many_clauses", raw: shorten(raw), ...(field ? { field } : {}) });
        vals = vals.slice(0, MAX_CLAUSE_VALUES);
      }
      if (!field) {
        // Unknown key: free text, unless a label group of that name exists (decided by the resolver).
        warnings.push({ code: "unknown_field", raw, field: m[1]!.toLowerCase() });
        const text = s.slice(i0, i);
        const ref = vals.length > 0 ? { key: m[1]!.toLowerCase(), values: vals } : undefined;
        clauses.push({ kind: "text", text, negate, raw, ...(ref ? { fieldRef: ref } : {}) });
        continue;
      }
      if (vals.length === 0) {
        warnings.push({ code: "invalid_value", raw, field: m[1]!.toLowerCase() });
        continue;
      }
      if (field === "sort") {
        for (const v of vals) {
          const parsed = parseSort(v);
          if (parsed) sort.push(parsed);
          else warnings.push({ code: "invalid_value", raw, field: "sort", value: v });
        }
        continue;
      }
      if (op !== "=" && !COMPARABLE_FIELDS.has(field)) {
        warnings.push({ code: "invalid_operator", raw, field });
        op = "=";
      }
      if (op !== "=" && vals.length > 1) {
        warnings.push({ code: "invalid_value", raw, field, value: vals.slice(1).join(",") });
        vals = vals.slice(0, 1);
      }
      clauses.push({ kind: "field", field, op, values: vals, negate, raw });
      continue;
    }

    let j = i;
    while (j < s.length && !isSpace(s[j])) j++;
    const word = s.slice(i, j);
    i = j;
    if (word) clauses.push({ kind: "text", text: word, negate, raw: s.slice(start, j) });
  }

  return { clauses, sort, warnings };
}

/**
 * The same limits for an AST that did not come from `parseQuery` (saved views, API bodies): clauses and
 * values beyond the caps are dropped with a `too_many_clauses` warning. Returns `ast` itself when within limits.
 */
export function limitAst(ast: QueryAst): QueryAst {
  const tooManyValues = ast.clauses.some(
    (c) => (c.kind === "field" ? c.values.length : (c.fieldRef?.values.length ?? 0)) > MAX_CLAUSE_VALUES,
  );
  if (ast.clauses.length <= MAX_QUERY_CLAUSES && ast.sort.length <= MAX_QUERY_CLAUSES && !tooManyValues) return ast;
  const clauses = ast.clauses.slice(0, MAX_QUERY_CLAUSES).map((c): QueryClause => {
    if (c.kind === "field")
      return c.values.length > MAX_CLAUSE_VALUES ? { ...c, values: c.values.slice(0, MAX_CLAUSE_VALUES) } : c;
    if (c.fieldRef && c.fieldRef.values.length > MAX_CLAUSE_VALUES)
      return { ...c, fieldRef: { ...c.fieldRef, values: c.fieldRef.values.slice(0, MAX_CLAUSE_VALUES) } };
    return c;
  });
  return {
    clauses,
    sort: ast.sort.slice(0, MAX_QUERY_CLAUSES),
    warnings: [...ast.warnings, { code: "too_many_clauses", raw: "" }],
  };
}

function quote(value: string): string {
  return /[\s,"]/.test(value) || value === "" ? `"${value.replaceAll('"', "")}"` : value;
}

/** Canonical text for one clause (filter chips). */
export function formatClause(clause: QueryClause): string {
  const neg = clause.negate ? "-" : "";
  if (clause.kind === "text" && clause.fieldRef)
    return `${neg}${clause.fieldRef.key}:${clause.fieldRef.values.map(quote).join(",")}`;
  if (clause.kind === "text") return `${neg}${/\s/.test(clause.text) ? `"${clause.text}"` : clause.text}`;
  const op = clause.op === "=" ? "" : clause.op;
  return `${neg}${clause.field}:${op}${clause.values.map(quote).join(",")}`;
}

/** Canonical query text for an AST (round-trips through parseQuery). */
export function formatQuery(ast: Pick<QueryAst, "clauses" | "sort">): string {
  const parts = ast.clauses.map(formatClause);
  for (const s of ast.sort) {
    const key = s.key === "group" ? (s.group ?? "") : s.key;
    parts.push(`sort:${key}${s.dir === DEFAULT_SORT_DIR[s.key] ? "" : `-${s.dir}`}`);
  }
  return parts.join(" ");
}
